/**
 * EntityCardWrapper - Wrapper component adding connection dot to entity cards
 *
 * Features:
 * - Connection dot on card edge for drag-to-link
 * - Visual feedback for link status
 * - Hover/drag state animations
 */

import { motion } from 'framer-motion';
import React, { useCallback, useRef } from 'react';

import { cn } from '@/lib/utils';

import type { ConnectionStatus, Point } from '../types/linking-view.types';

interface EntityCardWrapperProps {
  entityId: string;
  isLinked: boolean;
  linkStatus: ConnectionStatus | null;
  side: 'left' | 'right';
  onConnectionDotRef: (element: HTMLDivElement | null) => void;
  isDragSource: boolean;
  isValidDropTarget: boolean;
  isCurrentDropTarget: boolean;
  isDragDisabled?: boolean;
  onDragStart: (position: Point) => void;
  onDragEnd: () => void;
  children: React.ReactNode;
  className?: string;
  /** Optional sync badge to display on the card */
  syncBadge?: React.ReactNode;
}

/**
 * Get dot color based on link status
 */
function getDotColor(
  linkStatus: ConnectionStatus | null,
  isLinked: boolean,
): string {
  if (!isLinked) return 'bg-zinc-300 hover:bg-zinc-400';

  switch (linkStatus) {
    case 'approved':
      return 'bg-emerald-500';
    case 'suggested':
      return 'bg-amber-500';
    case 'manual':
      return 'bg-blue-500';
    default:
      return 'bg-zinc-400';
  }
}

/**
 * Get border/ring styles based on state
 */
function getBorderStyles(
  isCurrentDropTarget: boolean,
  isDragSource: boolean,
  linkStatus: ConnectionStatus | null,
): string {
  if (isCurrentDropTarget) {
    return 'ring-2 ring-blue-500 ring-offset-2 ring-offset-white';
  }
  if (isDragSource) {
    return 'ring-2 ring-blue-400 ring-offset-1 ring-offset-white';
  }
  if (linkStatus === 'approved') {
    return 'ring-1 ring-emerald-300 bg-emerald-50/30';
  }
  if (linkStatus === 'suggested') {
    return 'ring-1 ring-amber-300 bg-amber-50/30';
  }
  return '';
}

export const EntityCardWrapper: React.FC<EntityCardWrapperProps> = ({
  entityId,
  isLinked,
  linkStatus,
  side,
  onConnectionDotRef,
  isDragSource,
  isValidDropTarget,
  isCurrentDropTarget,
  isDragDisabled = false,
  onDragStart,
  onDragEnd,
  children,
  className,
  syncBadge,
}) => {
  const dotRef = useRef<HTMLDivElement>(null);

  // Handle drag start from connection dot
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't allow dragging if disabled (e.g., already has approved link)
      if (isDragDisabled) return;

      e.preventDefault();
      e.stopPropagation();

      if (dotRef.current) {
        const rect = dotRef.current.getBoundingClientRect();
        const containerRect = dotRef.current
          .closest('[data-linking-container]')
          ?.getBoundingClientRect() ?? { left: 0, top: 0 };

        onDragStart({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        });
      }
    },
    [onDragStart, isDragDisabled],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDragEnd();
    },
    [onDragEnd],
  );

  // Combined ref callback
  const setDotRef = useCallback(
    (el: HTMLDivElement | null) => {
      (dotRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      onConnectionDotRef(el);
    },
    [onConnectionDotRef],
  );

  const dotColor = getDotColor(linkStatus, isLinked);
  const borderStyles = getBorderStyles(
    isCurrentDropTarget,
    isDragSource,
    linkStatus,
  );

  return (
    <div
      className={cn(
        'relative rounded-lg transition-all duration-200',
        borderStyles,
        isValidDropTarget && !isCurrentDropTarget && 'opacity-80',
        isDragSource && 'shadow-md',
        className,
      )}
      data-entity-id={entityId}
    >
      {/* Entity card content */}
      {children}

      {/* Connection dot - positioned on the edge of the card */}
      <motion.div
        ref={setDotRef}
        className={cn(
          'absolute top-1/2 z-20 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white shadow-sm transition-all',
          side === 'right' ? '-right-2' : '-left-2',
          dotColor,
          isDragDisabled ? 'cursor-default' : 'cursor-grab',
          isDragSource && 'cursor-grabbing scale-110',
          isCurrentDropTarget && 'scale-125 ring-2 ring-blue-300 ring-offset-1',
        )}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        whileHover={isDragDisabled ? {} : { scale: 1.2 }}
        whileTap={isDragDisabled ? {} : { scale: 1.05 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        title={
          isDragDisabled
            ? 'Already linked'
            : isLinked
              ? `${linkStatus} link - drag to reconnect`
              : 'Drag to link with another entity'
        }
      />

      {/* Glow effect for drop targets */}
      {isCurrentDropTarget && (
        <motion.div
          className="absolute inset-0 -z-10 rounded-lg bg-blue-500/10"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1.02 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
        />
      )}

      {/* Sync suggestion badge for unlinked entities */}
      {syncBadge && (
        <div className="absolute top-2 right-2 z-20">{syncBadge}</div>
      )}
    </div>
  );
};

export default EntityCardWrapper;
